import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useRetrievePlaylistContentDuration, useUpdatePlaylistContentSettings } from '../../../../queries';
import { formatDurationCompact } from '../../../../config/global';

/**
 * Standalone-playlist-page tap-to-edit duration — .claude/pp-playlist-page-
 * prototype.html's `.dur`/`.dur-in`/`.dur-reset`. Functionally like the
 * campaign builder's BuilderDuration.jsx (same `.pp-cb-dur`/`.pp-cb-dur-in`
 * classes, same POST /playlist/media/setting save path via
 * useUpdatePlaylistContentSettings), but ALSO renders a reset-to-original
 * icon (`.pp-cb-dur-reset`) when the row's current duration differs from
 * the underlying media's own original duration — a control the campaign
 * builder's row doesn't have, so this is its own component rather than a
 * prop added to the shared one.
 *
 * ⚠️ `original` comes from `item.duration` — NOT `item.total_duration` (that
 * field doesn't exist on this endpoint's response at all; it's a
 * campaign_playlist_media-only column). Confirmed against
 * PlaylistMediaController::getPlaylistMedia()'s raw select: it selects
 * `playlist_media.duration as custom_duration` FIRST, then `playlist_media.*`
 * and `media.*` — both of which also carry an unaliased `duration` column,
 * and `media.*` is selected last, so the plain `duration` key in the
 * response is `media.duration` (the file's real/original length), while
 * `custom_duration` is the (possibly overridden) `playlist_media.duration`.
 */
function PlaylistItemDuration({ item, playlistId, layer }) {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(item.custom_duration);

  const updatePlaylistItemSetting = useUpdatePlaylistContentSettings();
  const retrieveDuration = useRetrievePlaylistContentDuration();
  const loading = updatePlaylistItemSetting.isLoading || retrieveDuration.isLoading;

  const current = Number(item.custom_duration) || 0;
  const original = Number(item.duration) || 0;
  const isCustom = original > 0 && current !== original;

  const commit = async () => {
    setEditing(false);
    const v = Number(value);
    if (!(v > 0) || v === current) return;
    await updatePlaylistItemSetting.mutateAsync({ playlist_media_id: item.playlist_media_id, duration: v });
  };

  const reset = async (e) => {
    e.stopPropagation();
    await retrieveDuration.mutateAsync(item.playlist_media_id);
  };

  if (editing) {
    return (
      <input
        type="number"
        min="1"
        autoFocus
        className="pp-cb-dur-in"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') e.currentTarget.blur();
          if (e.key === 'Escape') { setValue(current); setEditing(false); }
        }}
        disabled={loading}
      />
    );
  }

  return (
    <>
      <button
        type="button"
        className="pp-cb-dur"
        onClick={(e) => {
          e.stopPropagation();
          setValue(current);
          setEditing(true);
        }}
        title={t('dragdrop.edit_duration')}
      >
        {formatDurationCompact(current)}
      </button>
      {isCustom && (
        <button
          type="button"
          className="pp-cb-dur-reset"
          onClick={reset}
          disabled={loading}
          title={t('playlist.duration_reset_tooltip', { duration: formatDurationCompact(original) })}
        >
          <i className="pi pi-refresh" />
        </button>
      )}
    </>
  );
}

export default PlaylistItemDuration;
